import { Fragment, useEffect, useState } from "react";

import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import normalizeString from "../../../utils/normaliseString";


export default function ComboBoxInput({ label = "", options, searchField, valueField = "", register, setValue, registerName, defaultOption = false, initialValue = "", size = "md", classes = "", containerClasses = "", placeholder = "", disabled = false }) {
  const { t } = useTranslation("common");
  const [selected, setSelected] = useState("");
  const [query, setQuery] = useState('')

  const sizeClasses = {
    sm: "px-2 py-1 text-sm",
    md: "px-3 py-2 text-sm",
    lg: "px-4 py-3 text-base"
  };

  //Search the options based on the name or the ID
  const filteredOptions =
    query === '' ?
      options
      :
      options.filter((option) =>
        normalizeString(option[searchField])?.includes(normalizeString(query)) ||
        option.id?.toString().includes(query)
      );

  const handleSelect = (option) => {
    setSelected(option);

    if (valueField !== "" && option) {
      setValue(registerName, option[valueField])
    } else {
      setValue(registerName, option)
    }
  }

  useEffect(() => {
    if (defaultOption && options.length > 0) {
      setSelected(options[0])
      setValue(registerName, options[0])
    }

    if (initialValue) {
      const searchValue = initialValue.toString().toLowerCase().replace(/\s+/g, '');
      const initialOption = options.find(option =>
        option[searchField].toLowerCase().replace(/\s+/g, '') === searchValue ||
        option.id?.toString().toLowerCase().replace(/\s+/g, '') === searchValue ||
        (valueField && option[valueField]?.toString().toLowerCase().replace(/\s+/g, '') === searchValue)
      );

      setSelected(initialOption)

      //If the valueField is empty, the value will be the option object
      if (valueField === "") setValue(registerName, initialOption)
      else if (initialOption) setValue(registerName, initialOption[valueField])
    }
  }, [])

  return (
    <div className={containerClasses}>
      {label &&
        <label>
          <span className="text-sm font-medium text-gray-500">{label}</span>
        </label>
      }
      <Combobox value={selected} onChange={handleSelect}>
        <div className={`relative mt-1 ${classes}`}>
          <div className="relative w-full cursor-default overflow-hidden bg-white text-left sm:text-sm ">
            <ComboboxInput
              className={`w-full text-gray-900 border-1 border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 ${sizeClasses[size]}`}
              displayValue={(option) => option ? option[searchField] : ""}
              onChange={(event) => setQuery(event.target.value)}
              autoComplete="off"
              disabled={disabled}
              placeholder={placeholder}
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <ComboboxOptions
              className="absolute z-30 mt-1 max-h-60 w-full overflow-auto bg-white py-1 text-base rounded-b-md ring-1 ring-gray-400 ring-opacity-5 focus:outline-none sm:text-sm"
            >
              {filteredOptions.length === 0 && query !== '' ? (
                <div key={0} className="relative cursor-default select-none py-2 px-4 text-gray-700">
                  {t("Nothing found.")}
                </div>
              ) : (
                filteredOptions.slice(0, 100).map((option) => (
                  <ComboboxOption
                    key={option.id ?? option[searchField]}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-primary-600 text-white' : 'text-gray-900'
                      }`
                    }
                    value={option}
                  >
                    {({ selected, active }) => (
                      <>
                        {/* Used for eBay categories path */}
                        {option.path && <span className={`${active ? 'text-gray-200' : 'text-gray-500'} text-xs`}>{option.path}</span>}
                        <span
                          className={`block truncate ${selected ? 'font-medium' : 'font-normal'
                            }`}
                        >
                          {option[searchField]}
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-primary-600'
                              }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </ComboboxOption>
                ))
              )}
            </ComboboxOptions>
          </Transition>
        </div>
      </Combobox>
    </div>
  )
}